import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ImageOff, Users, ArrowRight } from "lucide-react";
import api from "../lib/api";

/**
 * <TopFundraisers /> — home-page widget showing the top 4 P2P fundraisers this month.
 * Backed by GET /api/fundraisers/leaderboard?since_days=30&limit=4.
 * Quietly renders nothing if no P2P fundraisers have received gifts in the window.
 */
const TopFundraisers = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/fundraisers/leaderboard", { params: { since_days: 30, limit: 4 } })
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null;

    // Empty state — encourage starting the first community fundraiser.
    if (items.length === 0) {
        return (
            <section className="py-20 bg-[#1C252E] text-white" data-testid="home-top-fundraisers-empty">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#E53935]/20 rounded-full mb-4">
                        <Trophy className="w-3 h-3 text-[#F2D8D5]" strokeWidth={2} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#F2D8D5]">Community fundraisers</span>
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-3">
                        No public fundraisers yet.
                    </h2>
                    <p className="text-sm text-white/70 mb-8 max-w-lg mx-auto">
                        Start the first community fundraiser — set your own goal, link to an active foundation campaign, and share with friends and family.
                    </p>
                    <Link
                        to="/account"
                        data-testid="top-fundraisers-empty-cta"
                        className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-3 text-sm font-medium transition-all"
                    >
                        Start the first community fundraiser
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <section className="py-20 bg-[#1C252E] text-white" data-testid="home-top-fundraisers">
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#E53935]/20 rounded-full mb-4">
                            <Trophy className="w-3 h-3 text-[#F2D8D5]" strokeWidth={2} />
                            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#F2D8D5]">Top fundraisers this month</span>
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                            Neighbors raising for neighbors.
                        </h2>
                        <p className="text-sm text-white/70 mt-3 max-w-md">
                            Teachers, parents, and community members running their own fundraising pages tied
                            to active foundation campaigns.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="top-fundraisers-grid">
                    {items.map((f, i) => {
                        const pct = f.goal_amount > 0
                            ? Math.min(100, Math.round((f.raised / f.goal_amount) * 100))
                            : 0;
                        return (
                            <Link
                                key={f.id}
                                to={`/fundraise/${f.slug}`}
                                data-testid={`top-fundraiser-card-${f.id}`}
                                className="group bg-white/5 hover:bg-white/10 backdrop-blur rounded-2xl overflow-hidden border border-white/10 transition-all flex flex-col"
                            >
                                <div className="relative">
                                    {f.photo_url ? (
                                        <img src={f.photo_url} alt="" className="w-full h-36 object-cover" />
                                    ) : (
                                        <div className="w-full h-36 bg-[#2D3748] flex items-center justify-center">
                                            <ImageOff className="w-10 h-10 text-white/30" strokeWidth={1.5} />
                                        </div>
                                    )}
                                    <div className="absolute top-3 left-3 bg-[#E53935] text-white text-[10px] font-bold rounded-full w-7 h-7 flex items-center justify-center">
                                        #{i + 1}
                                    </div>
                                </div>
                                <div className="p-4 flex-1 flex flex-col">
                                    <h3 className="font-heading text-base font-medium leading-tight mb-1 line-clamp-2 group-hover:text-[#F2D8D5] transition-colors">
                                        {f.title}
                                    </h3>
                                    <p className="text-xs text-white/60 mb-3 truncate">
                                        by {f.organizer_name}
                                    </p>
                                    <div className="mt-auto">
                                        <div className="flex items-baseline justify-between mb-1.5">
                                            <p className="font-heading text-lg font-medium">
                                                ${f.raised.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </p>
                                            <p className="text-[10px] text-white/60">
                                                {pct}% of ${f.goal_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </p>
                                        </div>
                                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-[#E53935]" style={{ width: `${pct}%` }} />
                                        </div>
                                        <p className="text-[11px] text-white/60 mt-2 flex items-center gap-1">
                                            <Users className="w-3 h-3" strokeWidth={2} />
                                            {f.donor_count} donor{f.donor_count === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                <div className="mt-8 text-center">
                    <Link
                        to="/account"
                        data-testid="top-fundraisers-cta"
                        className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-3 text-sm font-medium transition-all"
                    >
                        Start your own fundraiser
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default TopFundraisers;
